<script lang="ts" context="module">
    import Modal, { createModal as baseCreateModal } from '$components/Modal.svelte';
    import type { CreateDialogProps } from '@melt-ui/svelte';
    import * as t from '$trans';

    export function createVideoModal(config: CreateDialogProps) {
        return baseCreateModal({
            ...config,
            forceVisible: true,
            openFocus: (el) => {
                if (el != null) {
                    return (
                        el?.querySelector<HTMLElement>('[data-video-modal-auto-focus]') ??
                        null
                    );
                } else {
                    return (
                        document.querySelector<HTMLElement>(
                            '[data-video-modal-auto-focus]',
                        ) ?? null
                    );
                }
            },
        });
    }
</script>

<script lang="ts">
    import { melt } from '@melt-ui/svelte';
    import { fade } from 'svelte/transition';
    import { cx } from '$lib/utils/cx';
    import { getContainer } from '$components/Context/ContainerContext';

    const { lang } = getContainer();

    export let modal: ReturnType<typeof createVideoModal>;
    const {
        elements: { title, close },
        states: { open },
    } = modal;

    let isUrlValid = false;
    let displayErrorWhenUrlIsNotValid = false;
    let url: string = '';

    export let onUrl: (url: string) => void;

    function validateYoutubeOrVimeoUrl(url: string) {
        const youtubeRegex =
            /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
        const vimeoRegex = /^(http:\/\/|https:\/\/)?(www\.)?(vimeo\.com\/)([0-9]+)$/;

        return youtubeRegex.test(url) || vimeoRegex.test(url);
    }

    $: isUrlValid = validateYoutubeOrVimeoUrl(url);

    const validate = () => {
        if (isUrlValid) {
            $open = false;
            onUrl(url);
            url = '';
        } else {
            displayErrorWhenUrlIsNotValid = true;
        }
    };
</script>

<Modal
    defaultContentClass={cx(
        'bg-white',
        'overflow-hidden transition-all shadow-xl',
        'w-[600px]',
        'p-[20px]',
        'rounded-0 t:rounded-[8px]',
        'justify-start',
    )}
    backdropClass="bg-opacity-20"
    dialogClass="text-start"
    {modal}
>
    <div class="border-0 text-left">
        <button use:melt={$close} class="close hover:opacity-50">
            <span aria-hidden="true">&times;</span>
        </button>

        <div class="text-lg font-bold" use:melt={$title}>
            {t.ui.choose_video({}, $lang)}
        </div>
    </div>

    <div class="flex flex-col gap-8 my-8">
        <input
            bind:value={url}
            data-video-modal-auto-focus
            class={'control-form'}
            on:keypress={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    validate();
                }
            }}
            type="text"
            placeholder={t.feed_post.input_video_placeholder({}, $lang)}
        />

        {#if displayErrorWhenUrlIsNotValid && !isUrlValid}
            <p class="text-red-700 mt-1 flex flex-col gap-2" in:fade>
                {t.ui.url_of_video_not_valid({}, $lang)}
            </p>
        {/if}
    </div>

    <div class="flex flex-row-reverse">
        <button on:click|preventDefault={validate} class="btn-leo btn-leo-primary">
            <i class={'fa fa-check fa-lg'} aria-hidden={true} />
            {t.ui.choose_this_video({}, $lang)}
        </button>
    </div>
</Modal>
